import { useEffect, useRef, useState } from "react";
import { Bookmark, BookmarkCheck, Share2, Download, Link as LinkIcon, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { isSaved, saveArticle, removeSaved } from "@/lib/savedArticles";
import { sendReelEvent, reelShareUrl, reelDownloadUrl } from "@/lib/reels";

const Action = ({ onClick, label, text, active, testId, children, disabled, guard }) => (
    <button type="button" onClick={(e) => { e.stopPropagation(); if (guard?.current && Date.now() < guard.current) return; onClick(); }} disabled={disabled} aria-label={label} aria-pressed={active} data-testid={testId}
        className="flex flex-col items-center gap-[5px] text-white min-w-[48px] min-h-[58px] text-[11px] font-semibold leading-[1.5] disabled:opacity-60 group">
        <span className={`h-11 w-11 rounded-full flex items-center justify-center border backdrop-blur-md transition-[background-color,transform] group-active:scale-90 ${active ? "bg-brand-red border-brand-red" : "bg-[#08131675] border-white/20"}`}>{children}</span>
        <span className="whitespace-nowrap">{text}</span>
    </button>
);

/** Share fallback (no navigator.share): bottom sheet inside the player with the link + copy button, exactly as in the preview. */
function ShareSheet({ url, onClose, onCopied, fixed }) {
    const { t } = useLang();
    const [status, setStatus] = useState("");
    const input = useRef(null);
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    const copy = async () => {
        try { if (!navigator.clipboard?.writeText) throw new Error(); await navigator.clipboard.writeText(url); setStatus(t("linkCopied")); onCopied?.(); }
        catch { input.current?.focus(); input.current?.select(); setStatus(t("linkSelected")); }
    };
    return (
        <div className={`${fixed ? "fixed z-[80]" : "absolute z-10"} inset-0 bg-[#0310138c] flex items-end`} onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }} data-testid="reel-share-sheet">
            <section role="dialog" aria-modal="true" aria-label={t("shareStory")} className="w-full bg-white dark:bg-[#1a292c] text-brand-black dark:text-gray-100 rounded-t-[19px] p-4 shadow-2xl" onPointerDown={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-2 mb-4">
                    <h3 className="font-display font-bold text-base m-0">{t("shareStory")}</h3>
                    <button type="button" onClick={onClose} aria-label={t("close")} className="h-[38px] w-[38px] rounded-full border border-gray-200 dark:border-gray-600 flex items-center justify-center" data-testid="reel-share-close"><X className="w-[18px] h-[18px]" /></button>
                </div>
                <label className="sr-only" htmlFor="reel-share-link">{t("storyLink")}</label>
                <input id="reel-share-link" ref={input} readOnly value={url} dir="ltr" className="w-full border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-[#122023] rounded-lg p-[11px] text-xs mb-2.5" style={{ fontFamily: "Arial, sans-serif" }} data-testid="reel-share-link" />
                <button type="button" onClick={copy} className="w-full min-h-[44px] rounded-lg bg-brand-red text-white text-[13px] font-bold flex items-center justify-center gap-2" data-testid="reel-share-copy"><LinkIcon className="w-[17px] h-[17px]" /> {t("copyLink")}</button>
                <div role="status" className="text-[11px] text-gray-500 text-center min-h-[18px] mt-2" data-testid="reel-share-status">{status}</div>
            </section>
        </div>
    );
}

/** Vertical action rail (end side). Every action targets THE reel on screen. */
export default function ReelActions({ reel, source, onLikeChange, onSheet, suppressClickRef, variant = "rail", hidden = false }) {
    const { t, lang, p } = useLang();
    const [saved, setSaved] = useState(() => isSaved(reel.id));
    const [sheet, setSheet] = useState(false);
    const dlAt = useRef(0);
    useEffect(() => {
        setSaved(isSaved(reel.id));
        const refresh = () => setSaved(isSaved(reel.id));
        window.addEventListener("saved:change", refresh);
        return () => window.removeEventListener("saved:change", refresh);
    }, [reel.id]);
    useEffect(() => { onSheet?.(sheet); }, [sheet, onSheet]);
    useEffect(() => () => onSheet?.(false), [onSheet]);

    const save = () => {
        if (saved) {
            if (!removeSaved(reel.id).ok) { toast.error(t("saveFail")); return; }
            setSaved(false); toast(t("unsaved")); sendReelEvent("unsave", { articleId: reel.id, source, lang });
        } else {
            if (!saveArticle({ id: reel.id, slug: reel.slug, title: reel.title }).ok) { toast.error(t("saveFail")); return; }
            setSaved(true); toast.success(t("savedDone"), { description: t("savedNote") }); sendReelEvent("save", { articleId: reel.id, source, lang });
        }
    };
    const url = reelShareUrl(p, reel);
    const share = async () => {
        if (navigator.share) {
            try { await navigator.share({ title: reel.title, url }); sendReelEvent("share_click", { articleId: reel.id, source, lang, target: "native" }); return; }
            catch (e) { if (e?.name === "AbortError") return; }
        }
        sendReelEvent("share_click", { articleId: reel.id, source, lang, target: "sheet" });
        setSheet(true);
    };
    const [dlBusy, setDlBusy] = useState(false);
    const download = async () => {
        const now = Date.now();
        if (dlBusy || now - dlAt.current < 3000) return;   // repeated taps → one request
        dlAt.current = now;
        if (!String(reel.video_url || "").startsWith("/api/files/")) { toast.error(t("downloadFail")); return; }
        setDlBusy(true);
        sendReelEvent("download_request", { articleId: reel.id, source, lang });   // the click itself — never «downloaded»
        try {
            const head = await fetch(reelDownloadUrl(reel.id), { method: "HEAD", credentials: "include" });
            if (!head.ok) throw new Error(`head_${head.status}`);
            const a = document.createElement("a");
            a.href = reelDownloadUrl(reel.id); a.download = ""; a.rel = "noopener"; document.body.appendChild(a); a.click(); a.remove();
            toast(t("downloadStarted"), { description: t("downloadHint") });
        } catch { toast.error(t("downloadFail")); }
        finally { setDlBusy(false); }
    };

    return (
        <>
            <div className={variant === "side" ? "flex flex-col items-center gap-3" : variant === "row" ? "flex flex-row items-start justify-around gap-2 -mx-2" : `absolute end-[11px] flex flex-col items-center gap-2.5 z-[3] transition-[opacity,transform] duration-200 ${hidden ? "opacity-0 translate-x-3 pointer-events-none" : ""}`}
                style={variant === "rail" ? { bottom: "calc(230px + var(--reel-safe-b, 0px))" } : undefined} data-testid="reel-actions" data-variant={variant}>
                {/* Like button hidden from visitors (owner decision, 2026-06); likes stay stored and visible in the admin panel. */}
                <Action guard={suppressClickRef} onClick={save} label={saved ? t("savedDone") : t("saveArticle")} text={saved ? t("savedShort") : t("saveShort")} active={saved} testId="reel-save-button">
                    {saved ? <BookmarkCheck className="w-[22px] h-[22px]" /> : <Bookmark className="w-[22px] h-[22px]" />}
                </Action>
                <Action guard={suppressClickRef} onClick={share} label={t("share")} text={t("shareShort")} testId="reel-share-button"><Share2 className="w-[22px] h-[22px]" /></Action>
                <Action guard={suppressClickRef} onClick={download} label={t("download")} text={t("download")} testId="reel-download-button" disabled={dlBusy}><Download className="w-[22px] h-[22px]" /></Action>
            </div>
            {sheet && <ShareSheet fixed={variant !== "rail"} url={url} onClose={() => setSheet(false)} onCopied={() => sendReelEvent("copy_link", { articleId: reel.id, source, lang, target: "copy" })} />}
        </>
    );
}
